/**
 * This file implements the Auth interface (@see useAuth.ts ) for external token providers. This is
 * meant for use by the mobile apps to be able to inject their own tokens and respond to requests
 * from this site.
 *
 * The contract is as follows:
 *  - The mobile app will load this site in a webview and include query params `externalSSO=true`
 *    and `accessToken=<token>`.
 *  - This component will use that token for authentication.
 *  - When the token is close to expiring, this component can dispatch a request using either Webkit
 *    or Android bridges on the window.
 *  - After any refresh token request, we wait for the external app to inject the requested token by
 *    calling `window.externalAuthCallbacks.injectToken(token)`. This times out after 10 seconds.
 *  - The site can also request to update the correlation ID, like for impersonation. This results
 *    in a different bridge dispatch, which also awaits a new token to be injected with the desired
 *    correlation ID set.
 *
 * Important notes:
 *  - The mobile app is expected to handle the actual authentication flow and just provide us with tokens.
 *  - We don't implement the ability to log out.
 *  - We throw errors if the bridge functions don't exist, or don't respond in time.
 *  - The `externalSSO=true` opt-in is backed up in localStorage. If this browser needs to be reused
 *    without externalSSO enabled, you'll need to opt into a different auth flow like `spaSSO=true`,
 *    or clear localStorage.
 */
import { datadogRum } from "@datadog/browser-rum";
import { JwtPayload, parseJwt } from "@granular/fabric3-definitions";
import { type Auth } from "./useAuth";
import { useMemo, useSyncExternalStore } from "react";

/** We store a backup of our token in sessionStorage. Only import this for tests. */
export const EXTERNAL_AUTH_SESSION_TOKEN_KEY = "sso-external-auth-token";
const TOKEN_EXPIRE_EARLY_MS = 1000 * 60 * 10; // 10 minutes
const BRIDGE_READY_EVENT = "fabric3:external-auth-ready";
const BRIDGE_TOKEN_TIMEOUT_MS = 1000 * 10; // 10 seconds

// #region Store

type ExternalAuthStoreState = {
  token: string;
  tokenVersion: number;
};

function createExternalAuthStore() {
  const sessionToken = globalThis.sessionStorage.getItem(
    EXTERNAL_AUTH_SESSION_TOKEN_KEY,
  );

  let value: ExternalAuthStoreState | undefined = sessionToken
    ? {
        token: sessionToken,
        tokenVersion: 1,
      }
    : undefined;
  const listeners = new Set<() => void>();

  const getSnapshot = () => value;

  const subscribe = (listener: () => void) => {
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  };

  const notify = () => {
    for (const listener of listeners) {
      listener();
    }
  };

  /**
   * Updates the token in the store,
   * backs it up to sessionStorage,
   * and increments the token version number
   */
  const updateToken = (token: string) => {
    globalThis.sessionStorage.setItem(EXTERNAL_AUTH_SESSION_TOKEN_KEY, token);
    value = {
      token,
      tokenVersion: (value?.tokenVersion ?? 0) + 1,
    };
    notify();
  };

  /** Check if the token is expired. */
  const isTokenExpired = (expire_early_ms = TOKEN_EXPIRE_EARLY_MS) => {
    if (!value) return true;
    const claims = parseJwt<JwtPayload>(value.token);
    if (!claims) {
      throw new Error(
        "Failed to parse JWT claims when checking token expiration",
      );
    }
    const expiresOn = claims.exp ? claims.exp * 1000 : 0;
    const now = Date.now();
    return now >= expiresOn - expire_early_ms;
  };

  /** Do we have a valid token with a version more recent than the given startVersion */
  const isTokenUpdated = (startVersion: number) => {
    if (!value) return false;
    if (value.tokenVersion > startVersion && isTokenExpired()) {
      throw new Error(
        "When watching for a token update, found a new token that's already expired.",
      );
    }
    return value.tokenVersion > startVersion;
  };

  return {
    subscribe,
    getSnapshot,
    notify,
    updateToken,
    isTokenExpired,
    isTokenUpdated,
  } as const;
}

const externalAuthStore = createExternalAuthStore();

// #endregion
// #region Types

export type BridgeTokenRequest = {
  requestId: string;
  skipCache?: boolean;
};

type AndroidBridge = {
  requestAccessToken?: (request: BridgeTokenRequest) => void;
  setCorrelationId?: (correlationId: string | undefined) => void;
};

type WebkitBridge = {
  messageHandlers: {
    requestAccessToken?: {
      postMessage: (request: BridgeTokenRequest) => void;
    };
    setCorrelationId?: {
      postMessage: (correlationId: string | undefined) => void;
    };
  };
};

type ExternalAuthCallbacks = {
  injectToken: (token: string) => void;
};

declare global {
  interface Window {
    /** webkit JS bridge we can call when we want stuff  */
    webkit?: WebkitBridge;
    /** android JS bridge we can call when we want stuff */
    Mobile?: AndroidBridge;
    /** callbacks that the external host should hit to talk to us */
    externalAuthCallbacks?: ExternalAuthCallbacks;
    /** debug callbacks, not for use in code OR TESTS */
    externalAuthDebug?: {
      injectToken: (token: string) => void;
      dispatchTokenRequest: (skipCache?: boolean) => string;
      dispatchSetCorrelationId: (correlationId: string | undefined) => void;
      refreshAccessToken: () => Promise<string>;
    };
  }
}

const getAndroidBridge = (): AndroidBridge | undefined => {
  return globalThis.window.Mobile;
};

const getWebkitBridge = (): WebkitBridge | undefined => {
  return globalThis.window.webkit;
};

// #endregion
// #region Init

let initialized = false;
/**
 * Pull an access token from the URL to initialize external auth.
 * Sets up a callback on the window for external sources to inject a token in the future.
 */
export const initializeExternalSSO = () => {
  datadogRum.addAction("Initializing external auth", {
    feature: "auth",
  });
  globalThis.window.externalAuthCallbacks = {
    injectToken: externalAuthStore.updateToken,
  };

  globalThis.window.externalAuthDebug = {
    injectToken: externalAuthStore.updateToken,
    dispatchTokenRequest,
    dispatchSetCorrelationId,
    refreshAccessToken,
  };

  if (!externalAuthStore.getSnapshot()?.token) {
    // Attempt to pull a token from the URL.
    const url = new URL(globalThis.location.href);
    const token =
      url.searchParams.get("accessToken") ??
      url.searchParams.get("access_token");

    if (!token) {
      throw new Error(
        "No starting access token for external auth. Expected a token in query param 'accessToken'.",
      );
    }
    externalAuthStore.updateToken(token);
    url.searchParams.delete("accessToken");
    url.searchParams.delete("access_token");
    globalThis.history.pushState({}, "", url);
  }

  initialized = true;
  // When the token came from sessionStorage (any reload after the first), the store never changed,
  // so subscribers would otherwise never re-check `initialized` and AuthGuard would spin forever.
  externalAuthStore.notify();
  globalThis.window.dispatchEvent(new Event(BRIDGE_READY_EVENT));
};

const getIsInitialized = () => initialized;

/**
 * This is used to tell the outside world whether externalSSO is ready to use.
 *
 * The only thing we really need for that is a saved and valid token,
 * so that's what we check for here.
 *
 * There is other stuff to initialize,
 * but that's all internal and isn't needed until we actually want to logout or refresh.
 */
export const useIsInitializedExternalSSO = () => {
  const store = useSyncExternalStore(
    externalAuthStore.subscribe,
    externalAuthStore.getSnapshot,
  );
  const isInitialized = useSyncExternalStore(
    externalAuthStore.subscribe,
    getIsInitialized,
  );
  return store !== undefined && isInitialized;
};

// #endregion
// #region External Comms

/** Ask any available bridge for a fresh token */
const dispatchTokenRequest = (skipCache = false): string => {
  const requestId = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  const payload: BridgeTokenRequest = {
    requestId,
    skipCache,
  };

  let handled = false;

  const androidBridge = getAndroidBridge();
  if (androidBridge?.requestAccessToken) {
    androidBridge.requestAccessToken(payload);
    datadogRum.addAction("Android bridge token request", payload);
    handled = true;
  }

  const webkitBridge = getWebkitBridge();
  if (webkitBridge?.messageHandlers?.requestAccessToken?.postMessage) {
    webkitBridge.messageHandlers.requestAccessToken.postMessage(payload);
    datadogRum.addAction("Webkit bridge token request", payload);
    handled = true;
  }

  if (!handled) {
    const error = new Error(
      "No external auth token request handler found on window bridge.",
    );
    datadogRum.addError(error, {
      requestId,
    });
    throw error;
  }

  return requestId;
};

/** Inform any available bridge to update the correlation ID, and ask for a new token */
const dispatchSetCorrelationId = (correlationId: string | undefined) => {
  let handled = false;
  const androidBridge = getAndroidBridge();
  if (androidBridge?.setCorrelationId) {
    androidBridge.setCorrelationId(correlationId);
    datadogRum.addAction("Android bridge set correlation ID", {
      correlationId,
    });
    handled = true;
  }

  const webkitBridge = getWebkitBridge();
  if (webkitBridge?.messageHandlers?.setCorrelationId?.postMessage) {
    webkitBridge.messageHandlers.setCorrelationId.postMessage(correlationId);
    datadogRum.addAction("Webkit bridge set correlation ID", { correlationId });
    handled = true;
  }

  if (!handled) {
    const error = new Error(
      "No external auth setCorrelationId handler found on window bridge.",
    );
    datadogRum.addError(error, {
      correlationId,
    });
    throw error;
  }
};

/** Returns a promise that waits until an external source hits `injectToken` with a new token */
const waitForTokenInjection = async (
  startVersion: number,
  timeoutMs = BRIDGE_TOKEN_TIMEOUT_MS,
): Promise<string> => {
  if (externalAuthStore.isTokenUpdated(startVersion)) {
    return externalAuthStore.getSnapshot()!.token;
  }

  return new Promise<string>((resolve, reject) => {
    const timeout = globalThis.setTimeout(() => {
      unsubscribe();
      reject(
        new Error("Timed out waiting for external access token injection."),
      );
    }, timeoutMs);

    const unsubscribe = externalAuthStore.subscribe(() => {
      if (externalAuthStore.isTokenUpdated(startVersion)) {
        globalThis.clearTimeout(timeout);
        unsubscribe();
        resolve(externalAuthStore.getSnapshot()!.token);
      }
    });
  });
};

/** We store this refresh promise globally so concurrent refreshes can await the one in progress */
let refreshPromise: Promise<string> | undefined;

/** Ask for a new token and wait for it to be injected */
const refreshAccessToken = async (): Promise<string> => {
  if (refreshPromise) return refreshPromise;

  const startVersion = externalAuthStore.getSnapshot()?.tokenVersion ?? 0;

  refreshPromise = (async () => {
    dispatchTokenRequest(true /* skipCache */);
    return waitForTokenInjection(startVersion);
  })();

  try {
    return await refreshPromise;
  } finally {
    refreshPromise = undefined;
  }
};

/** Ask to update correlation ID and wait for a new token */
const setCorrelationId = async (correlationId: string | undefined) => {
  const startVersion = externalAuthStore.getSnapshot()?.tokenVersion ?? 0;
  dispatchSetCorrelationId(correlationId);
  await waitForTokenInjection(startVersion);
};

/** Ask to unset correlation ID and wait for a new token */
const unsetCorrelationId = async () => {
  const startVersion = externalAuthStore.getSnapshot()?.tokenVersion ?? 0;
  dispatchSetCorrelationId(undefined);
  await waitForTokenInjection(startVersion);
};

// #endregion
// #region Auth Interface

/**
 * Our async token getter. If the current token is valid, returns that.
 * Otherwise, asks the external token provider for a new token.
 */
const getAccessToken = async (): Promise<string> => {
  return externalAuthStore.isTokenExpired()
    ? // Our token is expired, ask for a new one.
      refreshAccessToken()
    : // Our token is valid, return it.
      externalAuthStore.getSnapshot()!.token;
};

/** Necessary for the Auth contract, but should not be called in external auth. Just logs errors. */
const logout = () => {
  // For external auth, we can't really log out. Send an error to the RUM and invalidate the token.
  const error = new Error(
    "Logout called in external auth implementation. This is not supported, but the token will be cleared.",
  );
  datadogRum.addError(error);
  throw error;
};

/**
 * This hook is the entrypoint to external auth.
 * It's called under any implementation due to the _rules of hooks_,
 * but its contents should only actually be used when using "externalSSO".
 */
export const useExternalSSO = (): Auth | undefined => {
  const store = useSyncExternalStore(
    externalAuthStore.subscribe,
    externalAuthStore.getSnapshot,
  );

  const auth = useMemo(() => {
    if (!store) return;
    return {
      ssoImplementation: "externalSSO" as const,
      getAccessToken,
      refreshAccessToken,
      renderingToken: store.token,
      logout,
      setCorrelationId,
      unsetCorrelationId,
    };
  }, [store]);

  return auth;
};

// #endregion
